<template lang="html">
    <report-section
        :id="id"
        title="Landcover"
    >
        <p class="text-section">
            The landcover<sup>
                <footnote-tooltip
                    :footnote-number="16"
                />
            </sup> characteristics influence hydrologic processes in a watershed<sup>
                <footnote-tooltip
                    :footnote-number="17"
                />
            </sup>. The chart below shows the landcover makeup of the {{ reportData.overview.watershedName }}
            watershed. These components  were incorporated in the hydrologic model that produces the water supply
            estimates in this report, primarily influencing the evapotranspiration component of the water budget
            calculations, which represent the amount of water that moves directly back to the atmosphere through
            direct evaporation or transpiration by vegetation.
        </p>
        <div class="visuals">
            <pie-chart
                class="landcover-pie-chart"
                :chart-info="landcoverChartInfo"
                :provided-options="chartOptions"
            />
            <watershed-characteristics-table
                class="landcover-table"
                :data="landcoverData"
            />
        </div>
    </report-section>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';
import PieChart from '@foundryspatial/pie-chart';
import sectionMixin from '@foundryspatial/report-section/mixin';
import WatershedCharacteristicsTable from '@foundryspatial/watershed-characteristics-table';

import scssVars from '@/assets/scss/_variables.scss';

export default {
    name: 'Landcover',

    components: {
        FootnoteTooltip,
        WatershedCharacteristicsTable,
        PieChart,
    },

    mixins: [
        sectionMixin,
    ],

    data: () => ({
        // Initial data, will be overwritten with DB values
        landcoverData: [
            {
                key: 'coniferous',
                title: 'Coniferous',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverConiferous,
            },
            {
                key: 'barren',
                title: 'Barren',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverBarren,
            },
            {
                key: 'shrub',
                title: 'Shrub',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverShrub,
            },
            {
                key: 'deciduous',
                title: 'Deciduous',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverDeciduous,
            },
            {
                key: 'water',
                title: 'Water',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverWater,
            },
            {
                key: 'mixed',
                title: 'Mixed',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverMixed,
            },
            {
                key: 'herb',
                title: 'Herb',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverHerb,
            },
            {
                key: 'grassland',
                title: 'Grassland',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverGrassland,
            },
            {
                key: 'snow',
                title: 'Snow / Glacier',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverSnowGlacier,
            },
            {
                key: 'wetland',
                title: 'Wetland',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverWetland,
            },
            {
                key: 'developed',
                title: 'Developed',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverDeveloped,
            },
            {
                key: 'cropland',
                title: 'Cropland',
                area: 0,
                percentage: 0,
                color: scssVars.colorWatershedLandcoverCropland,
            },
        ],
    }),

    computed: {
        landcoverChartInfo() {
            return {
                nameKey: 'title',
                percentageKey: 'percentage',
                colorKey: 'color',
                data: this.landcoverData,
            };
        },
        chartOptions() {
            return {
                sort: true,
            };
        },
    },

    created() {
        const data = this.reportData.overview;
        // Set landcover percentages and area from rollup
        this.landcoverData.forEach((point, index) => {
            this.landcoverData[index].percentage = data[point.key];
            this.landcoverData[index].area = (data[point.key] / 100)
                * data.area_km2;
        });
    },
};
</script>

<style lang="scss">
#landcover.report-section {
    .visuals {
        display: flex;
        flex-direction: row;
        align-items: center;

        > * {
            margin: 0 3em;
        }

        .landcover-pie-chart {
            margin: 0 3em;
            height: 300px;
            width: 300px;
        }

        .landcover-table {
            flex: 1;

            thead {
                th {
                    border-width: 0 0 1px;
                }
            }

            tbody {
                td {
                    border: 1px solid $light-grey-accent;
                    border-width: 0 0 1px;
                }
            }
        }

        @media screen and (max-width: $widescreen) {
            .landcover-pie-chart {
                height: 250px;
                width: 250px;
            }
        }

        @media screen and (max-width: $desktop) {
            flex-direction: column;

            > * {
                margin: 2em 1em;
            }

            .landcover-table {
                align-self: stretch;
            }
        }

        @media screen and (max-width: $tablet) {
            .landcover-pie-chart {
                height: 200px;
                width: 200px;
            }
        }
    }
}

.static-report #landcover.report-section {
    p {
        line-height: 20px;
    }

    .visuals {
        justify-content: space-between;

        .landcover-pie-chart {
            height: 240px;
            width: 240px;
            margin: 0 30px;
        }

        .landcover-table {
            flex: unset;
            width: 370px;
            font-size: 10px;
            margin: 0;

            tr {
                height: 20px;

                .icon-column {
                    padding-top: 0;
                    padding-bottom: 0;

                    .icon {
                        margin-bottom: -5px;
                    }
                }
            }
        }
    }
}
</style>
